import React from 'react';
import { createContext, PropsWithChildren, useContext } from 'react';
import { useApiGet } from '../Common/Api/useApiGet';
import env from '../env';

export type Maybe<T> = T | null | undefined;

export interface TreePlantingResponse {
  todaysTreePlantingTick?: number;
  totalNumberOfTreesPlanted: number;
  parnerId?: string;
  partnerName?: string;
  partnerLogo?: string;
}

export interface TreePlantingContextProviderProps {
  partnerId?: string;
}

export interface ITreePlantingContext {
  treesPlanted?: Maybe<number>;
  tonnesOfCo2?: Maybe<number>;
  squareMeteresOfIceSaved?: Maybe<number>;
  milesDriven?: Maybe<number>;
  parnerId?: string;
  partnerName?: string;
  partnerLogo?: string;
  showTreesPlantedByPartner?: boolean;
  todaysTreePlantingTick?: number;
}

const defaultState: ITreePlantingContext = {
  treesPlanted: 0,
  tonnesOfCo2: 0,
  squareMeteresOfIceSaved: 0,
  milesDriven: 0,
  showTreesPlantedByPartner: false,
  todaysTreePlantingTick: 0,
};

export const TreePlantingContext =
  createContext<ITreePlantingContext>(defaultState);

export const useTreePlantedContext = () => useContext(TreePlantingContext);

export const TreePlantingContextProvider: React.FC<
  PropsWithChildren<TreePlantingContextProviderProps>
> = ({ children, partnerId }) => {
  const pathSuffix = partnerId ? `partner/${partnerId}` : 'all';

  const { data } = useApiGet<TreePlantingResponse>(
    `${env.api_url}/partners/platform/Trees/${pathSuffix}`
  );

  /** tonnes of Co2 = total number of trees * 0.3 */
  const tonnesOfCo2 =
    data && +(data?.totalNumberOfTreesPlanted * 0.3).toFixed(2);
  /** 2481 miles = 1 tonne of Co2 emmissions */
  const milesDriven = tonnesOfCo2 && +(tonnesOfCo2 * 2481).toFixed(2);
  /** 0.3 meter squared of ice = 1 tonne of Co2  */
  const squareMeteresOfIceSaved =
    tonnesOfCo2 && +(tonnesOfCo2 * 0.3).toFixed(2);

  return (
    <TreePlantingContext.Provider
      value={{
        treesPlanted:
          data?.totalNumberOfTreesPlanted ?? defaultState.tonnesOfCo2,
        tonnesOfCo2: tonnesOfCo2 ?? defaultState.tonnesOfCo2,
        milesDriven: milesDriven ?? defaultState.milesDriven,
        squareMeteresOfIceSaved:
          squareMeteresOfIceSaved ?? defaultState.squareMeteresOfIceSaved,
        partnerLogo: data?.partnerLogo,
        parnerId: data?.parnerId,
        partnerName: data?.partnerName,
        showTreesPlantedByPartner: !!partnerId,
        todaysTreePlantingTick:
          data?.todaysTreePlantingTick ?? defaultState.todaysTreePlantingTick,
      }}
    >
      {children}
    </TreePlantingContext.Provider>
  );
};
